import { documentFormatter } from "@/utils/documentFormatter";
import {
  Table,
  Thead,
  Tr,
  Th,
  Box,
  Tbody,
  Td,
  Text,
  Flex,
  Tooltip,
  useColorModeValue,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { Role } from "@/models/role.model";
import { PermissionEnum } from "@/utils/permissionEnum";
import { AiOutlineEye } from "react-icons/ai";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { IConsignadoProposal } from "@/models/consignado/proposal.model";
import ProposalModalConsignado from "../ProposalModal";
import { ProposalStatus, StatusRenderer } from "@/utils/consignado/proposalStatusRenderer";
import { isLeilaoOrigem, LEILAO_LABEL } from "@/utils/consignado/proposalOrigem";
import { toTitleCase } from "@/components/CreditAuctionProposalsPage/formatters";
import { getOfertaEscolhida } from "@/utils/consignado/ofertaEscolhida";

// Larguras fixas (tableLayout: "fixed") pra cabeçalho e colunas não
// variarem de tamanho conforme os dados da página — sempre reservam
// espaço pro maior conteúdo esperado daquela coluna.
const COLUMN_WIDTHS = {
  acoes: "60px",
  nomeCliente: "150px",
  cpf: "100px",
  corban: "140px",
  promotora: "140px",
  vendedor: "140px",
  tabela: "130px",
  valorLiberado: "110px",
  dataProposta: "110px",
  dataFormalizacao: "130px",
  dataPgto: "110px",
  status: "140px",
  originacao: "100px",
};

interface ProposalNewTableProps {
  proposals: IConsignadoProposal[];
  sortBy: string;
  sortDirection: string;
  setSortBy: (field: string) => void;
  setSortDirection: (field: string) => void;
}

export default function ProposalNewTableConsignado({
  proposals,
  setSortBy,
  setSortDirection,
  sortBy,
  sortDirection,
}: ProposalNewTableProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const backgroundStyle = useConditionalBackground();
    const [currentProposal, setCurrentProposal] = useState<IConsignadoProposal | null>(null);
  const authStorageString = localStorage.getItem("auth-storage");
  const authStorage = authStorageString ? JSON.parse(authStorageString) : null;
  const userRoles = authStorage?.state?.user?.roles || [];
  const userPermissions = userRoles.flatMap((role: Role) => role.permissions);
  const hasPermission =
    userPermissions.includes(PermissionEnum.GRANATECH) ||
    userPermissions.includes(PermissionEnum.ADMIN);

  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");

  const handleSort = (field: string) => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "asc" ? "desc" : "asc"
    );
  };

  if (!proposals.length) {
    return null;
  }

  return (
    <>
      <Box>
        <Box overflow="auto" borderRadius="md" boxShadow="md">
          <Table size="sm" sx={{ tableLayout: "fixed" }}>
            <Thead
              {...backgroundStyle}
              position="sticky"
              top={0}
              zIndex={1}
              boxShadow="md"
            >
              <Tr>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.acoes} whiteSpace="normal" wordBreak="break-word">
                  Ações
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.nomeCliente} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>NOME DO CLIENTE</Text>
                    <IconButton
                      icon={
                        sortBy === "id" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by name"
                      bg={"gray.200"}
                      onClick={() => handleSort("id")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.cpf} whiteSpace="normal" wordBreak="break-word">
                  CPF DO CLIENTE
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.corban} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>CORBAN</Text>
                    <IconButton
                      icon={
                        sortBy === "corban" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by corban"
                      bg={"gray.200"}
                      onClick={() => handleSort("corban")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.promotora} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>PROMOTORA</Text>
                    <IconButton
                      icon={
                        sortBy === "promoter" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by promoter"
                      bg={"gray.200"}
                      onClick={() => handleSort("promoter")}
                    />
                  </HStack>
                </Th>
                {hasPermission && (
                  <Th
                    color={headerColor}
                    textAlign="center"
                    fontSize="xs"
                    py={5}
                    w={COLUMN_WIDTHS.vendedor}
                    whiteSpace="normal" wordBreak="break-word"
                  >
                    <HStack justify="center">
                      <Text>VENDEDOR</Text>
                      <IconButton
                        icon={
                          sortBy === "seller" && sortDirection === "asc" ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        }
                        variant="ghost"
                        size="1"
                        fontSize={"1rem"}
                        borderRadius={"lg"}
                        aria-label="Sort by seller"
                        bg={"gray.200"}
                        onClick={() => handleSort("seller")}
                      />
                    </HStack>
                  </Th>
                )}

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.tabela} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>TABELA</Text>
                    <IconButton
                      icon={
                        sortBy === "product" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by product"
                      bg={"gray.200"}
                      onClick={() => handleSort("product")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.valorLiberado} whiteSpace="normal" wordBreak="break-word">
                  VALOR LIBERADO
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.dataProposta} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>DATA DA PROPOSTA</Text>
                    <IconButton
                      icon={
                        sortBy === "operationDate" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by operationDate"
                      bg={"gray.200"}
                      onClick={() => handleSort("operationDate")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.dataFormalizacao} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>DATA DA FORMALIZAÇÃO</Text>
                    <IconButton
                      icon={
                        sortBy === "lastFormalizationDate" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by lastFormalizationDate"
                      bg={"gray.200"}
                      onClick={() => handleSort("lastFormalizationDate")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.dataPgto} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>DATA DO PGTO</Text>
                    <IconButton
                      icon={
                        sortBy === "disbursementDate" &&
                        sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by disbursementDate"
                      bg={"gray.200"}
                      onClick={() => handleSort("disbursementDate")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.status} whiteSpace="normal" wordBreak="break-word">
                  <HStack justify="center">
                    <Text>STATUS DA PROPOSTA</Text>
                    <IconButton
                      icon={
                        sortBy === "status" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by status"
                      bg={"gray.200"}
                      onClick={() => handleSort("status")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5} w={COLUMN_WIDTHS.originacao} whiteSpace="normal" wordBreak="break-word">
                  ORIGINAÇÃO
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {proposals?.map((proposal, index) => (
                <Tr
                  key={index}
                  _hover={{ bg: rowHoverBg }}
                  transition="all 0.3s ease-in-out"
                  borderBottom="1px solid"
                  borderColor={rowBorderColor}
                >
                  <Td textAlign="center" fontSize="sm" py={3}>
                    <Tooltip label="Visualizar" aria-label="Visualizar Tooltip" placement="top">
                      <Flex
                        justify="center"
                        align="center"
                        cursor="pointer"
                        onClick={() => {
                          setCurrentProposal(proposal);
                          setIsModalOpen(true);
                        }}
                      >
                        <AiOutlineEye fontSize="1.5rem" color="teal.500" />
                      </Flex>
                    </Tooltip>
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {toTitleCase(proposal.nome_cliente)}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.cpf
                      ? documentFormatter(proposal.cpf)
                      : "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {isLeilaoOrigem(proposal?.origem)
                      ? LEILAO_LABEL
                      : toTitleCase(proposal?.nome_corban)}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {isLeilaoOrigem(proposal?.origem)
                      ? LEILAO_LABEL
                      : toTitleCase(proposal?.nome_promotora)}
                  </Td>
                  {hasPermission && (
                    <Td textAlign="center" fontSize="sm" py={3}>
                      {isLeilaoOrigem(proposal?.origem)
                        ? LEILAO_LABEL
                        : toTitleCase(proposal?.nome_vendedor)}
                    </Td>
                  )}
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal.produto_nome || "-"}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {BrlCurrencyFormatter(
                      getOfertaEscolhida(proposal.custom_status).oferta?.releasedAmount
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.data_proposta ? (
                      <>
                        <Box>
                          {new Date(
                            proposal.data_proposta
                          ).toLocaleDateString("pt-BR")}
                        </Box>
                        <Box fontSize="xs">
                          {new Date(
                            proposal.data_proposta
                          ).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Box>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.data_formalizacao ? (
                      <>
                        <Box>
                          {new Date(
                            proposal.data_formalizacao
                          ).toLocaleDateString("pt-BR")}
                        </Box>
                        <Box fontSize="xs">
                          {new Date(
                            proposal.data_formalizacao
                          ).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Box>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.data_pagamento ? (
                      <>
                        <Box>
                          {new Date(
                            proposal.data_pagamento
                          ).toLocaleDateString("pt-BR")}
                        </Box>
                        <Box fontSize="xs">
                          {new Date(
                            proposal.data_pagamento
                          ).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Box>
                      </>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    <StatusRenderer
                      status={proposal.status as ProposalStatus}
                      customStatus={proposal.custom_status as string}
                    />
                  </Td>
                  <Td textAlign="center" fontSize="sm" py={3}>
                    {proposal?.nome_fundo ?? "-"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      <ProposalModalConsignado
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        proposalId={currentProposal?.id_proposta!}
      />
    </>
  );
}
